// ─────────────────────────────────────────────────────────────
// Tutorial.jsx
// Overlay de tutorial con mini-tutoriales interactivos.
// Usa getBoundingClientRect para detectar la posición real
// de los botones en cualquier tamaño de pantalla.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";

// ── Calcula la posición de un elemento por su id ──────────────
// Devuelve { top, left, width, height } relativo al viewport
function getElPos(id) {
  const el = document.getElementById(id);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom };
}

// ── Componente de flecha que apunta a un elemento ─────────────
function Arrow({ targetId, darkMode }) {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    setPos(null);
    // Intentar varias veces por si el elemento tarda en renderizar
    let attempts = 0;
    const tryFind = () => {
      const p = getElPos(targetId);
      if (p) {
        setPos(p);
      } else if (attempts < 10) {
        attempts++;
        setTimeout(tryFind, 100);
      }
    };
    tryFind();
  }, [targetId]);

  if (!pos) return null;

  // Centro horizontal del elemento
  const arrowLeft = pos.left + pos.width / 2 - 14;
  // Justo debajo del elemento
  const arrowTop = pos.bottom + 6;

  return (
    <div style={{
      position: "fixed",
      top: arrowTop,
      left: arrowLeft,
      zIndex: 300,
      pointerEvents: "none",
      fontSize: 60,
      animation: "whislu-bounce 0.8s ease-in-out infinite",
    }}>
      👆
    </div>
  );
}

export default function Tutorial({
  lang,
  darkMode,
  tutorialStep,
  setTutorialStep,
  setShowTutorial,
  setActiveTab,
  setScreen,
  setCurrentFamily, userFamilies,
}) {
  const [miniStep, setMiniStep] = useState(null);

  // ── Pasos principales ─────────────────────────────────────
  const steps = [
    {
      tab: "wishlu",
      emoji: "🎁",
      title:
        lang === "es" ? "¡Bienvenido a Wishlu!" :
        lang === "fi" ? "Tervetuloa Wishluun!" :
        "Welcome to Wishlu!",
      text:
        lang === "es" ? "Aquí en \"Mi Wishlu\" podrás empezar a enlistar tus deseos y compartirlos con quien quieras." :
        lang === "fi" ? "Täällä \"Oma Wishlu\" -osiossa voit alkaa listata toiveitasi ja jakaa ne kenen tahansa kanssa." :
        "Here in \"My Wishlu\" you can start listing your wishes and share them with anyone you want.",
      highlight: "wishlu",
      hasMini: true,
    },
    {
      tab: "groups",
      emoji: "👥",
      title:
        lang === "es" ? "Crea tu grupo" :
        lang === "fi" ? "Luo ryhmäsi" :
        "Create your group",
      text:
        lang === "es" ? "Crea un grupo con tu familia para no perderte ningún cumpleaños, saber exactamente qué desean y organizar intercambios navideños. ¡No olvides activar el Santa Secreto! 🎅" :
        lang === "fi" ? "Luo ryhmä perheesi kanssa, jotta et missaa syntymäpäiviä ja tiedät tarkalleen mitä he haluavat. Älä unohda aktivoida Salainen Joulupukki! 🎅" :
        "Create a group with your family so you never miss a birthday, know exactly what they want and organize gift exchanges. Don't forget to activate Secret Santa! 🎅",
      highlight: "groups",
      hasMini: true,
    },
    {
      tab: "profile",
      emoji: "👤",
      title:
        lang === "es" ? "Tu perfil" :
        lang === "fi" ? "Profiilisi" :
        "Your profile",
      text:
        lang === "es" ? "Termina de editar tu perfil añadiendo métodos de pago para recibir cooperaciones. Navega la app y si necesitas algo, ¡contáctanos!" :
        lang === "fi" ? "Viimeistele profiilisi lisäämällä maksutavat yhteisrahoituksen vastaanottamiseksi. Selaa sovellusta ja ota meihin yhteyttä tarvittaessa!" :
        "Finish editing your profile by adding payment methods to receive contributions. Browse the app and if you need anything, contact us!",
      highlight: "profile",
      hasMini: false,
    },
  ];

  // ── Mini pasos Wishlu ─────────────────────────────────────
  const miniWishlu = [
    {
      targetId: "tutorial-add-gift",
      text:
        lang === "es"
          ? "Aquí puedes agregar los regalos que deseas recibir o que no quieres olvidar comprar. Pega el link y toca \"Auto\" para rellenar automáticamente. Si solo escribes el título, irá a \"siempre viene bien\". 🎁"
          : lang === "fi"
          ? "Täällä voit lisätä lahjoja. Liitä linkki ja napauta \"Auto\". Jos kirjoitat vain otsikon, se menee \"sopii aina\" -osioon. 🎁"
          : "Add gifts you want to receive. Paste a link and tap \"Auto\" to fill automatically. Title only? It goes to \"always a good gift\". 🎁",
    },
    {
      targetId: "tutorial-share",
      text:
        lang === "es"
          ? "¿Se acerca tu cumpleaños? ¡Comparte tu Wishlu para que todos sepan qué regalarte! 🎂"
          : lang === "fi"
          ? "Lähestyykö syntymäpäiväsi? Jaa Wishlu-listasi! 🎂"
          : "Is your birthday coming up? Share your Wishlu so everyone knows what to gift you! 🎂",
    },
  ];

  // ── Mini pasos Grupos ─────────────────────────────────────
  const miniGrupos = [
    {
      targetId: "tutorial-group-item",
      text:
        lang === "es"
          ? "Mantén presionado un grupo para editarlo: cambia el nombre o elige un emoticon. 🌙"
          : lang === "fi"
          ? "Pidä ryhmää painettuna muokataksesi: vaihda nimi tai valitse emoji. 🌙"
          : "Long press a group to edit: change the name or choose an emoji. 🌙",
    },
    {
      targetId: "tutorial-add-managed",
      text:
        lang === "es"
          ? "El botón + te permite manejar más cuentas aparte de la tuya. ¡Especial para tus pequeñines! 👶"
          : lang === "fi"
          ? "+-nappi antaa sinun hallita useampia tilejä. Erityisesti pikkusille! 👶"
          : "The + button lets you manage more accounts. Perfect for your little ones! 👶",
    },
  ];

  const step = steps[tutorialStep];
  const isLast = tutorialStep === steps.length - 1;
  const currentMini = step.tab === "wishlu" ? miniWishlu : step.tab === "groups" ? miniGrupos : null;
  const miniData = miniStep !== null && currentMini ? currentMini[miniStep] : null;
  const isLastMini = miniStep !== null && currentMini && miniStep === currentMini.length - 1;

  // Pestañas con nombres
  const tabLabels = [
    lang === "es" ? "Mi Wishlu" : lang === "fi" ? "Oma Wishlu" : "My Wishlu",
    lang === "es" ? "Grupos" : lang === "fi" ? "Ryhmät" : "Groups",
    lang === "es" ? "Perfil" : lang === "fi" ? "Profiili" : "Profile",
  ];

  // ── Cerrar todo ───────────────────────────────────────────
  const cerrar = () => {
    setShowTutorial(false);
    setMiniStep(null);
    try { localStorage.setItem("whislu_tutorial_done", "1"); } catch (e) {}
  };

  // ── Siguiente del mini ────────────────────────────────────
  const siguienteMini = () => {
  if (isLastMini) {
    setMiniStep(null);
    const next = steps[tutorialStep + 1];
    setActiveTab(next.tab);
    setScreen("family");
    setTutorialStep(tutorialStep + 1);
  } else {
    // Si estamos en grupos paso 0 → abrir el primer grupo para mostrar el +
    if (tutorialStep === 1 && miniStep === 0 && userFamilies?.length > 0) {
      setCurrentFamily(userFamilies[0]);
    }
    setMiniStep(miniStep + 1);
  }
};

  return (
    <>
      {/* Fondo oscuro */}
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300 }} />

      {/* Flecha apuntando al elemento — solo en mini activo */}
      {miniData && <Arrow targetId={miniData.targetId} darkMode={darkMode} />}

      {/* Dedo apuntando a la pestaña — solo en paso normal */}
      {!miniData && (
        <div style={{
          position: "fixed", bottom: 40, left: 0, right: 0,
          display: "flex", justifyContent: "flex-start",
          paddingLeft:
  step.highlight === "wishlu"  ? "10%"  :
  step.highlight === "groups"  ? "40%"  :
  "72%",
          zIndex: 302, pointerEvents: "none",
        }}>
          <div style={{ fontSize: 40, animation: "whislu-bounce 0.8s ease-in-out infinite" }}>👇</div>
        </div>
      )}

      {/* Caja del tutorial */}
      <div style={{
        position: "fixed", bottom: 80, left: 12, right: 16,
        background: darkMode ? "#1e1e1e" : "#fff",
        borderRadius: 20, padding: "24px 20px",
        zIndex: 301,
        boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        border: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"),
      }}>

        {/* ── Pestañas de navegación ── */}
        <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid " + (darkMode ? "#2e2e2e" : "#e8e2d9"), paddingBottom: 12 }}>
          {tabLabels.map((label, i) => (
            <button
              key={i}
              onClick={() => {
                setMiniStep(null);
if (steps[i].tab === "groups") {
  setCurrentFamily(null);
  try { localStorage.removeItem("whislu_last_family_id"); } catch(e) {}
}
setTutorialStep(i);
setActiveTab(steps[i].tab);
setScreen("family");
              }}
              style={{
                flex: 1,
                background: i === tutorialStep ? "#d4a853" : "transparent",
                border: "1px solid " + (i === tutorialStep ? "#d4a853" : (darkMode ? "#2e2e2e" : "#e0dbd2")),
                borderRadius: 20,
                padding: "4px 2px",
                fontSize: 10,
                fontWeight: i === tutorialStep ? 600 : 400,
                color: i === tutorialStep ? "#fff" : (darkMode ? "#888" : "#aaa"),
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Contenido: mini o paso normal ── */}
        {miniData ? (
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            {/* Indicadores del mini */}
            <div style={{ display: "flex", gap: 5, justifyContent: "center", marginBottom: 14 }}>
              {currentMini.map((_, i) => (
                <div key={i} style={{
                  width: i === miniStep ? 16 : 5,
                  height: 5, borderRadius: 3,
                  background: i === miniStep ? "#d4a853" : (darkMode ? "#3a3a3a" : "#e0dbd2"),
                  transition: "width 0.3s",
                }} />
              ))}
            </div>
            <p style={{ fontSize: 14, color: darkMode ? "#e8e2d9" : "#333", lineHeight: 1.6 }}>
              {miniData.text}
            </p>
          </div>
        ) : (
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>{step.emoji}</div>
            <h3 style={{ fontFamily: "Playfair Display, serif", fontSize: 20, marginBottom: 10, color: darkMode ? "#e8e2d9" : "#1a1a1a" }}>
              {step.title}
            </h3>
            <p style={{ fontSize: 14, color: "#999", lineHeight: 1.6 }}>{step.text}</p>
          </div>
        )}

        {/* ── Botones ── */}
        {miniData ? (
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-dorado" style={{ flex: 2 }} onClick={siguienteMini}>
              {isLastMini
                ? (lang === "es" ? "¡Entendido! 🎉" : lang === "fi" ? "Selvä! 🎉" : "Got it! 🎉")
                : (lang === "es" ? "Siguiente →" : lang === "fi" ? "Seuraava →" : "Next →")}
            </button>
            <button className="btn btn-gris" onClick={cerrar}>
              {lang === "es" ? "Saltar" : lang === "fi" ? "Ohita" : "Skip"}
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: step.hasMini ? 8 : 0 }}>
              {tutorialStep > 0 && (
                <button
                  className="btn btn-gris"
                  style={{ flex: 1 }}
                  onClick={() => {
                    const prev = steps[tutorialStep - 1];
                    setActiveTab(prev.tab);
                    setScreen("family");
                    setTutorialStep(tutorialStep - 1);
                    setMiniStep(null);
                  }}
                >
                  ← {lang === "es" ? "Anterior" : lang === "fi" ? "Edellinen" : "Back"}
                </button>
              )}
              <button
                className="btn btn-dorado"
                style={{ flex: 2 }}
                onClick={() => {
                  if (isLast) {
                    cerrar();
                    setActiveTab("profile");
                    setScreen("family");
                  } else {
                    const next = steps[tutorialStep + 1];
if (next.tab === "groups") {
  setCurrentFamily(null);
  try { localStorage.removeItem("whislu_last_family_id"); } catch(e) {}
}
setActiveTab(next.tab);
setScreen("family");
setTutorialStep(tutorialStep + 1);
                  }
                }}
              >
                {isLast
                  ? (lang === "es" ? "¡Entendido! 🎉" : lang === "fi" ? "Selvä! 🎉" : "Got it! 🎉")
                  : (lang === "es" ? "Siguiente →" : lang === "fi" ? "Seuraava →" : "Next →")}
              </button>
            </div>

            {/* Saber más */}
            {step.hasMini && (
              <button
                className="btn btn-gris"
                style={{ width: "100%", fontSize: 13 }}
                onClick={() => setMiniStep(0)}
              >
                {lang === "es" ? "Saber más →" : lang === "fi" ? "Lue lisää →" : "Learn more →"}
              </button>
            )}

            {/* Saltar */}
            {!isLast && (
              <div style={{ textAlign: "center", marginTop: 12 }}>
                <span style={{ fontSize: 12, color: "#bbb", cursor: "pointer" }} onClick={cerrar}>
                  {lang === "es" ? "Saltar tutorial" : lang === "fi" ? "Ohita" : "Skip tutorial"}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
